import { useState } from "react";
import { useI18n } from "../../i18n";

const SHOTS = ["dashboard_main", "drawdown_guard", "risk_meter", "equity_curve", "prop_rules_panel"];

// SEO-friendly descriptive alt text — improves Google Image search + accessibility
const SEO_ALTS = [
  "Dhokiyas Prop Passer Gold EA live MT5 rule dashboard showing drawdown, target progress and gold chart",
  "Dhokiyas Prop Passer Gold EA drawdown guard showing daily loss and max drawdown protection",
  "Dhokiyas Prop Passer Gold EA risk meter showing risk per trade, no martingale and no grid controls",
  "Dhokiyas Prop Passer Gold EA equity curve dashboard showing controlled account growth analytics",
  "Dhokiyas Prop Passer Gold EA prop rules panel showing daily loss, max drawdown, consistency and news filter status",
];

export default function Dashboard() {
  const [active, setActive] = useState(0);
  const { t } = useI18n();
  const labelKeys = ["dash_label1", "dash_label2", "dash_label3", "dash_label4", "dash_label5"];

  return (
    <section id="dashboard" className="relative py-16 sm:py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
        <div className="lg:col-span-5">
          <span className="chip">{t("dashboard_chip")}</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-5 leading-[1.05]">
            {t("dashboard_h1_a")} <span className="text-[color:var(--green)]">{t("dashboard_h1_b")}</span>
          </h2>
          <p className="text-[color:var(--ink-dim)] mt-4 leading-relaxed text-sm sm:text-base">{t("dashboard_sub")}</p>
          <ul className="mt-5 sm:mt-6 space-y-2.5">
            {["dash_b1", "dash_b2", "dash_b3", "dash_b4", "dash_b5"].map((k) => (
              <li key={k} className="flex items-center gap-3 text-[13px] sm:text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--gold)] shrink-0" />
                <span className="text-[color:var(--ink-dim)]">{t(k)}</span>
              </li>
            ))}
          </ul>

          {/* Thumbnail selectors — uses 320x180 WebP thumbs (~5KB each) for fast load */}
          <div className="mt-7 sm:mt-8 flex flex-wrap gap-2">
            {SHOTS.map((s, i) => (
              <button key={s} data-testid={`dashboard-thumb-${i}`} onClick={() => setActive(i)}
                aria-label={`Show ${t(labelKeys[i])}`}
                className={`w-16 h-12 sm:w-20 sm:h-14 rounded-md overflow-hidden border transition-all ${
                  active === i ? "border-[color:var(--gold)] ring-2 ring-[color:var(--gold)]/40" : "border-[#23232a] opacity-60 hover:opacity-100"
                }`}>
                <img
                  src={`/generated/thumbs/${s}.webp`}
                  alt={SEO_ALTS[i]}
                  width="320" height="180"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="panel p-3 sm:p-4 relative">
            <div className="flex items-center gap-2 px-2 pb-3">
              <span className="w-2.5 h-2.5 rounded-full bg-[#ff5c5c]/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-[color:var(--gold)]/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-[color:var(--green)]/70" />
              <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-widest text-[color:var(--ink-mute)] ml-2 truncate">
                {t(labelKeys[active])}
              </span>
            </div>
            <div className="rounded-lg overflow-hidden border border-[#23232a] bg-[#0a0a0b]">
              <img
                src={`/generated/${SHOTS[active]}.webp`}
                alt={SEO_ALTS[active]}
                width="1400" height="788"
                className="w-full object-cover"
                loading="lazy"
                decoding="async"
                fetchpriority={active === 0 ? "high" : "auto"}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
